/**
 * Wizard domain types — the configuration the user assembles before starting
 * a debate. Mirrors the backend `WizardPersonasRequest/Response` contract.
 *
 * Pure types + tiny pure helpers; no store / transport dependency.
 */
import type { CouncilSource } from "./personaLibrary";
import type {
  QuestionQualityReport,
  RefinedQuestion,
} from "../transport/questionQualityClient";
import type { CouncilCandidate } from "../transport/wizardClient";
import type {
  AgentRole,
  DebateTopology,
  InformationAsymmetry,
  Persona,
  SamplingOverrides,
} from "./types";

export type CritiqueStrictness = "loose" | "standard" | "strict";

/**
 * Konsey Cognitive Renaissance — Bölüm A0/A1.
 * Erken sonlanma profili. Wizard'da segmented control olarak sunulur,
 * backend `DebateEngineOptions.termination_profile` üzerinden okunur.
 *
 *   • loose     — finalize_min %30, threshold 0.88, deadlock_delta 0.04
 *   • balanced  — finalize_min %50, threshold 0.92, deadlock_delta 0.025
 *                 (default; sycophancy detector "warn")
 *   • strict    — finalize_min %75, threshold 0.95, deadlock_delta 0.015
 *                 (sycophancy detector "strict" → otomatik Devil's Advocate)
 */
export type TerminationProfile = "loose" | "balanced" | "strict";

/**
 * Görev 4 — urgency override sent to the backend's `question_router`.
 * `null` lets the heuristic classifier decide from the query text.
 */
export type Urgency = "crisis" | "operational" | "strategic";

/**
 * Görev 5 — structured decision constraints collected in the wizard and
 * forwarded verbatim as `StartDebateBody.constraints`. Mirrors the
 * backend's `_CONSTRAINT_HINTS` keys (`decision_framing.py`) so a
 * filled-in field here suppresses the matching clarifying question.
 * All free text — the backend only needs presence/absence + content,
 * never a strict shape.
 */
export interface DecisionConstraints {
  budget?: string;
  timeline?: string;
  riskTolerance?: string;
  complianceNotes?: string;
}

// NOTE.
//
// We previously had a 5th step reserved for RAG document upload; it
// was never wired into the UI (SetupWizard's stepper only iterates
// [1..4]). To prevent type/runtime drift we collapse to 4 steps until
// the upload flow is shipped.
export type WizardStep = 1 | 2 | 3 | 4;

export type WizardGenerationStatus = "idle" | "generating" | "ready" | "error";

/* ----------------------------------------------------------------------
 * Soru kalite kapısı (adım 1)
 * -------------------------------------------------------------------- */

export type QuestionQualityStatus =
  | "idle"
  | "assessing"
  | "ready"
  | "refining"
  | "error";

/** Kullanıcının bir netleştirici soruya verdiği yanıt (seçenek + serbest metin). */
export interface QuestionAnswerDraft {
  /** Seçilmiş seçenek id'leri. `allowMultiple` false ise en fazla bir eleman. */
  selected: string[];
  /** "Diğer" alanına yazılan serbest metin. */
  other: string;
}

/**
 * Adım 1 soru-kalite kapısının UI durumu.
 *
 * Kapı ASLA bloklamaz — puanlar, gerekçelendirir, rehberli bir yeniden
 * yazım önerir. `dismissed` true olduğunda kullanıcı düşük puana rağmen
 * devam etmeyi seçmiştir ve aynı soru metni için bir daha sorulmaz.
 */
export interface QuestionQualityState {
  status: QuestionQualityStatus;
  /** Son değerlendirmenin sonucu. */
  report: QuestionQualityReport | null;
  /** Değerlendirmenin yapıldığı soru metni — metin değişirse rapor bayatlar. */
  assessedQuery: string | null;
  error: string | null;
  /** Netleştirici soru id'si → kullanıcı taslağı. */
  answers: Record<string, QuestionAnswerDraft>;
  /** Son yeniden yazım önerisi; kullanıcı onaylayana kadar uygulanmaz. */
  proposal: RefinedQuestion | null;
  /** Kullanıcı bu soru için kapıyı bilinçli olarak geçti. */
  dismissed: boolean;
}

/**
 * Taze bir kalite durumu üretir.
 *
 * Fabrika fonksiyonu, sabit bir nesne DEĞİL: `answers` iç içe bir sözlük
 * olduğu için paylaşılan bir sabiti sığ kopyalamak (`{...DEFAULTS}`) tüm
 * sıfırlamaların AYNI `answers` referansını taşımasına yol açardı.
 */
export function freshQuestionQuality(): QuestionQualityState {
  return {
    status: "idle",
    report: null,
    assessedQuery: null,
    error: null,
    answers: {},
    proposal: null,
    dismissed: false,
  };
}

export const QUESTION_QUALITY_DEFAULTS: QuestionQualityState = freshQuestionQuality();

/** Rapor, o an ekranda yazılı olan soruya mı ait? */
export function isQualityReportFresh(
  state: QuestionQualityState,
  query: string,
): boolean {
  return state.report !== null && state.assessedQuery === query.trim();
}

/** Persona shape returned by `/api/debate/wizard/personas`. */
export interface WizardPersona extends Persona {
  /** One-line stance summary (≤120 chars). */
  stanceSummary: string;
}

/* ----------------------------------------------------------------------
 * Aday konsey kataloğu (adım 3)
 * -------------------------------------------------------------------- */

export type CandidateStatus = "idle" | "loading" | "ready" | "error";

/**
 * Adım 3'ün "Adaylardan Seç" kolu.
 *
 * Operatör geri bildirimi: otomatik üretim tek kutba kayıyor (önce hep
 * finansal, sonra hep teknik), elle unvan yazmak ise sorunun hangi yirmi
 * uzmanlığı hak ettiğini önceden bilmeyi gerektiriyor. Katalog aradaki
 * adım: sistem aday üretir, seçim operatörde kalır.
 */
export interface CandidateState {
  status: CandidateStatus;
  items: CouncilCandidate[];
  categories: string[];
  /** Seçili aday id'leri — koltuk sırası bu dizinin sırasıdır. */
  selected: string[];
  error: string | null;
  /** Katalogun üretildiği soru — soru değişirse liste bayatlar. */
  builtForQuery: string | null;
  /** Seçilenler personaya dönüştürülürken kaç tanesi tamamlandı. */
  materializedCount: number;
}

export function freshCandidateState(): CandidateState {
  return {
    status: "idle",
    items: [],
    categories: [],
    selected: [],
    error: null,
    builtForQuery: null,
    materializedCount: 0,
  };
}

export interface WizardConfig {
  step: WizardStep;
  query: string;
  topology: DebateTopology;
  agentCount: number;
  maxRounds: number;
  autoFinalize: boolean;
  critiqueStrictness: CritiqueStrictness;
  /** Konsey Cognitive Renaissance — Bölüm A0/A1. Default `balanced`. */
  terminationProfile: TerminationProfile;
  personas: WizardPersona[];
  generationStatus: WizardGenerationStatus;
  generationError: string | null;
  /** Monotonic seed for deterministic regeneration. */
  generationSeed: number | null;
  /** User-supplied debate-wide custom prompt. */
  userDebatePrompt: string | null;
  /** User-supplied moderator-specific prompt override. */
  userModeratorPrompt: string | null;
  /** Parent debate id when this wizard is pre-filled from a fork. */
  parentDebateId: string | null;
  /**
   * Görev 16 precursor — when true, public web retrieval backends must
   * never see the query. Forwarded as `StartDebateBody.private_mode`;
   * the backend also force-enables it when the query itself carries a
   * classification marker (GİZLİ, CONFIDENTIAL, …) regardless of this
   * toggle.
   */
  privateMode: boolean;
  /** Görev 4 — urgency override; `null` = let the heuristic decide. */
  urgency: Urgency | null;
  /** Görev 5 — structured constraints; filled fields suppress the matching clarifying question. */
  constraints: DecisionConstraints;
  /**
   * Konseyin nereden geleceği. `null` ⇒ kullanıcı HENÜZ SEÇMEDİ.
   *
   * Bu alanın varlığı bilinçli bir UX düzeltmesidir: adım 3'e girer girmez
   * otomatik LLM üretimi tetikleniyordu, dolayısıyla kütüphaneden takım/birim
   * seçme ihtimali kullanıcıya hiç sunulmadan bir konsey (ve LLM maliyeti)
   * oluşuyordu. Artık önce kaynak seçilir, üretim yalnız `"auto"` seçilirse
   * çalışır.
   */
  councilSource: CouncilSource | null;
  /**
   * Persona Kütüphanesi — seçili kalıcı takım. Doluyken adım 3'teki
   * dinamik persona üretimi ATLANIR: kullanıcı zaten kimliği belli bir
   * konsey seçmiştir, LLM'e yeniden uzman icat ettirmek hem para hem
   * kimliği bozar.
   */
  libraryTeamId: string | null;
  /** Seçili organizasyon birimi — alt birimler dahil tüm personaları gelir. */
  libraryUnitId: string | null;
  /**
   * Adım 1 soru-kalite kapısı. Kalıcılaştırılmaz (bkz. `debateStore`
   * persist whitelist'i): rapor bir LLM anlık görüntüsüdür, reload
   * sonrası bayat bir puanı taze gibi göstermek yanıltıcı olur.
   */
  quality: QuestionQualityState;
  /**
   * Takımdan bağımsız, tek tek seçilmiş kalıcı personalar. Takımla
   * birlikte verilirse backend üyelerin ARDINA ekler ve tekilleştirir.
   */
  libraryPersonaIds: string[];
  /** Adım 3 — aday konsey kataloğu (`councilSource === "catalog"`). */
  candidates: CandidateState;
}

export const WIZARD_DEFAULTS: WizardConfig = {
  step: 1,
  query: "",
  topology: "round-robin",
  agentCount: 5,
  maxRounds: 5,
  autoFinalize: true,
  critiqueStrictness: "standard",
  terminationProfile: "balanced",
  personas: [],
  generationStatus: "idle",
  generationError: null,
  generationSeed: null,
  userDebatePrompt: null,
  userModeratorPrompt: null,
  parentDebateId: null,
  privateMode: false,
  urgency: null,
  constraints: {},
  councilSource: null,
  libraryTeamId: null,
  libraryUnitId: null,
  libraryPersonaIds: [],
  quality: freshQuestionQuality(),
  candidates: freshCandidateState(),
};

/* ----------------------------------------------------------------------
 * Validation
 * -------------------------------------------------------------------- */

export const WIZARD_LIMITS = {
  queryMin: 12,
  queryMax: 1500,
  // Council scale extended to support marathon scenarios:
  //   - up to 20 specialists for cross-functional war games / red-blue
  //   - up to 50 rounds for deep iterative consensus reviews
  // Backend has matching guards (`agent_count<=20`, `max_rounds<=50`).
  agentCountMin: 3,
  agentCountMax: 20,
  maxRoundsMin: 2,
  maxRoundsMax: 50,
} as const;

/**
 * Adım 1'den çıkılabilir mi?
 *
 * Bu bir YUMUŞAK kapıdır — hiçbir koşulda operatörü kilitlemez:
 *   • `dismissed` → kullanıcı bilinçli olarak devam etmeyi seçti,
 *   • `error`     → değerlendirici erişilemiyor; LLM kesintisi yüzünden
 *                   kimse tartışma başlatmaktan alıkonulamaz,
 *   • `ready`     → iki değerlendirici de soruyu tartışmaya uygun buldu.
 *
 * Diğer tüm durumlarda "İleri" önce ölçümü tetikler, sonuç ekranda
 * gösterilir; ikinci tık (ya da "Yine de devam et") geçişi yapar.
 */
export function isQualityGateCleared(cfg: WizardConfig): boolean {
  const q = cfg.quality;
  if (q.dismissed || q.status === "error") return true;
  if (!isQualityReportFresh(q, cfg.query)) return false;
  return q.report?.verdict === "ready";
}

export function isStepValid(step: WizardStep, cfg: WizardConfig): boolean {
  // Defensive: every consumer assumes `cfg.personas` is an array, but
  // immer/zustand persistence corner cases can leave it undefined. We
  // refuse to advance past Step 2 when the array is malformed so a
  // half-built council can never reach the launch step.
  const personas = Array.isArray(cfg.personas) ? cfg.personas : [];
  const agentCount =
    Number.isFinite(cfg.agentCount) && cfg.agentCount > 0
      ? cfg.agentCount
      : WIZARD_DEFAULTS.agentCount;
  // Persona Kütüphanesi — kalıcı bir konsey seçildiğinde koltuklar zaten
  // doludur, dinamik üretim çalışmaz ve `personas` boş kalır. Bu durumda
  // `agentCount` eşiğini uygulamak adım 3'ü kilitler: konseyi seçtiği hâlde
  // "Tartışmayı Başlat" pasif kalırdı.
  const libraryCount =
    (cfg.libraryTeamId ? 1 : 0) +
    (cfg.libraryUnitId ? 1 : 0) +
    (cfg.libraryPersonaIds?.length ?? 0);
  switch (step) {
    case 1:
      return cfg.query.trim().length >= WIZARD_LIMITS.queryMin;
    case 2:
      return (
        agentCount >= WIZARD_LIMITS.agentCountMin &&
        agentCount <= WIZARD_LIMITS.agentCountMax &&
        cfg.maxRounds >= WIZARD_LIMITS.maxRoundsMin &&
        cfg.maxRounds <= WIZARD_LIMITS.maxRoundsMax
      );
    case 3:
    case 4:
      return libraryCount > 0 || personas.length >= agentCount;
  }
}

/* ----------------------------------------------------------------------
 * Cost projection (used by Step 4 summary)
 * -------------------------------------------------------------------- */

export interface CostEstimate {
  approxSeconds: number;
  approxTokens: number;
}

export function estimateDebateCost(cfg: WizardConfig): CostEstimate {
  return {
    approxSeconds: cfg.maxRounds * cfg.agentCount * 4,
    approxTokens: cfg.maxRounds * cfg.agentCount * 800,
  };
}

/* ----------------------------------------------------------------------
 * Persona patches (edit drawer)
 * -------------------------------------------------------------------- */

export interface PersonaPatch {
  displayName?: string;
  role?: AgentRole;
  expertise?: string;
  systemInstruction?: string;
  themeColor?: string;
  asymmetry?: InformationAsymmetry;
  stanceSummary?: string;
  modelId?: string;
  gender?: "male" | "female";
  honorific?: "Bey" | "Hanım";
  /** Edge-TTS rate token, e.g. "+5%". */
  voiceRate?: string;
  /** Edge-TTS pitch token, e.g. "+25Hz". */
  voicePitch?: string;
  /** Edge-TTS volume token, e.g. "+5%". */
  voiceVolume?: string;
  /**
   * Per-persona LLM sampling overrides — temperature, top_p, max_tokens,
   * presence/frequency penalty. Wired into `editPersona` so the slice
   * can both (a) update the local zustand state and (b) PATCH the
   * backend so customisations survive into the actual debate session.
   */
  samplingOverrides?: SamplingOverrides;
}
